import React, { useEffect, useMemo, useState } from 'react';
import { notesApi } from '@/lib/notesApi';
import { Loader2 } from 'lucide-react';
import { BatchTagItem, buildTagDataFromDetail, computeDefaultLayout } from '@/components/jewellery/JewelleryTagViewer';

const SEL = 'h-8 rounded-lg border border-input bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]';
const ready = (f) => (f.conditions || []).length > 0 && f.conditions.every((c) => ['empty', 'not_empty'].includes(c.op) || (c.value || '').trim());

// Live sample tag: saved active rules with the rule being edited swapped in / appended.
export function TagRulePreview({ items, defaultId, saved, draft, editId }) {
  const [id, setId] = useState(defaultId || items[0]?.id || '');
  const [view, setView] = useState('expanded');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (!id && (defaultId || items[0]?.id)) setId(defaultId || items[0].id); }, [defaultId, items, id]);
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    notesApi.post('/notes/tag-workspace/tags/batch', { jewellery_ids: [id] })
      .then((r) => setData(r.data.items?.[0] ? buildTagDataFromDetail(r.data.items[0]) : null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);
  const rules = useMemo(() => {
    const base = saved.filter((r) => r.active !== false);
    if (!ready(draft)) return base;
    const d = { ...draft, id: editId || '__draft', active: true };
    return editId ? base.map((r) => (r.id === editId ? d : r)).concat(base.some((r) => r.id === editId) ? [] : [d]) : [...base, d];
  }, [saved, draft, editId]);
  return (
    <div className="rounded-xl border border-border bg-background p-3" data-testid="tagws-rule-preview">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span className="font-semibold">Live preview</span>
        <select value={id} onChange={(e) => setId(e.target.value)} className={`${SEL} max-w-[220px]`} data-testid="tagws-rule-preview-piece">
          {items.map((it) => <option key={it.id} value={it.id}>{it.jewellery_no}</option>)}
        </select>
        <select value={view} onChange={(e) => setView(e.target.value)} className={SEL} data-testid="tagws-rule-preview-view">
          <option value="expanded">Expanded</option>
          <option value="collapsed">Collapsed</option>
        </select>
        {ready(draft) ? <span className="text-[hsl(var(--ring))]">Includes the rule you are editing</span> : <span>Fill the conditions to include this rule</span>}
      </div>
      <div className="flex min-h-[140px] items-start justify-center overflow-auto rounded-lg bg-secondary/40 p-2" data-testid="tagws-rule-preview-tag">
        {loading ? <Loader2 size={16} className="mt-10 animate-spin text-muted-foreground" />
          : data ? <BatchTagItem data={data} mode="full" layoutId={computeDefaultLayout(data, view)} rmMode={view} showPieces showQr={false} rules={rules} />
            : <span className="mt-10 text-xs text-muted-foreground">{items.length ? 'Could not load this piece.' : 'No pieces to preview.'}</span>}
      </div>
    </div>
  );
}
